import { isValidUUID, ThreadCommentVisibilityEnum } from '@scrambl/shared'

/** Persistent ownership survives a login; access leases never do. */
export interface ThreadDraftOwner {
  userId: string
  organizationId: string
  portalEntityId: string | null
  externalRelationshipId: string | null
}
export interface ThreadDraft<Attachment = unknown> {
  version: 1
  owner: ThreadDraftOwner
  content: string
  attachments?: Attachment[]
  mentionedUserIds?: string[]
  visibility?: ThreadCommentVisibilityEnum
  updatedAt: number
}
export interface ThreadDraftData<Attachment = unknown> {
  drafts: Record<string, ThreadDraft<Attachment>>
  // Ownerless or malformed entries remain recoverable, never auto-adopted.
  quarantinedDrafts: Record<string, unknown>
}
export function threadDraftOwnerKey(
  owner: ThreadDraftOwner | null,
): string | null {
  if (
    !owner ||
    !isValidUUID(owner.userId) ||
    !isValidUUID(owner.organizationId) ||
    (owner.portalEntityId !== null && !isValidUUID(owner.portalEntityId)) ||
    (owner.externalRelationshipId !== null &&
      !isValidUUID(owner.externalRelationshipId))
  )
    return null
  return JSON.stringify([
    owner.userId,
    owner.organizationId,
    owner.portalEntityId,
    owner.externalRelationshipId,
  ])
}
export function threadDraftKey(
  owner: ThreadDraftOwner | null,
  threadId: string,
  parentCommentId?: string | null,
): string | null {
  const ownership = threadDraftOwnerKey(owner)
  if (!ownership || !isValidUUID(threadId)) return null
  // Anchor composers have a distinct, opaque local key alongside ordinary replies.
  return JSON.stringify([ownership, threadId, parentCommentId ?? null])
}

/** Validate the envelope on every hydration, including the current version. */
export function restoreThreadDrafts<Attachment = unknown>(
  persisted: unknown,
): ThreadDraftData<Attachment> {
  const record =
    persisted && typeof persisted === 'object'
      ? (persisted as Record<string, unknown>)
      : {}
  const drafts: ThreadDraftData<Attachment>['drafts'] = {}
  const quarantinedDrafts =
    record.quarantinedDrafts &&
    typeof record.quarantinedDrafts === 'object' &&
    !Array.isArray(record.quarantinedDrafts)
      ? { ...(record.quarantinedDrafts as Record<string, unknown>) }
      : {}
  const entries =
    record.drafts &&
    typeof record.drafts === 'object' &&
    !Array.isArray(record.drafts)
      ? Object.entries(record.drafts)
      : []
  for (const [key, value] of entries) {
    const draft =
      value && typeof value === 'object'
        ? (value as ThreadDraft<Attachment>)
        : null
    let owned = false
    try {
      const parsed = JSON.parse(key)
      owned =
        !!draft &&
        draft.version === 1 &&
        typeof draft.content === 'string' &&
        Number.isFinite(draft.updatedAt) &&
        draft.updatedAt >= 0 &&
        (draft.visibility === undefined ||
          Object.values(ThreadCommentVisibilityEnum).includes(
            draft.visibility,
          )) &&
        (!draft.attachments || Array.isArray(draft.attachments)) &&
        (!draft.mentionedUserIds ||
          (Array.isArray(draft.mentionedUserIds) &&
            draft.mentionedUserIds.every(id => typeof id === 'string'))) &&
        Array.isArray(parsed) &&
        parsed.length === 3 &&
        (parsed[2] === null || typeof parsed[2] === 'string') &&
        key === threadDraftKey(draft.owner, parsed[1], parsed[2])
    } catch {
      /* Legacy keys have no ownership envelope. */
    }
    if (owned && draft) drafts[key] = draft
    else {
      let retainedKey = key
      let suffix = 1
      while (
        Object.prototype.hasOwnProperty.call(quarantinedDrafts, retainedKey)
      )
        retainedKey = `${key}#${++suffix}`
      Object.defineProperty(quarantinedDrafts, retainedKey, {
        value,
        enumerable: true,
        configurable: true,
        writable: true,
      })
    }
  }
  return { drafts, quarantinedDrafts }
}

/** One account's activity cannot evict another owner's saved work. */
export function boundThreadDrafts<Attachment>(
  drafts: Record<string, ThreadDraft<Attachment>>,
  owner: ThreadDraftOwner,
): Record<string, ThreadDraft<Attachment>> {
  const ownership = threadDraftOwnerKey(owner)
  if (!ownership) return drafts
  const owned = Object.entries(drafts).filter(
    ([, draft]) => threadDraftOwnerKey(draft.owner) === ownership,
  )
  if (owned.length <= 50) return drafts
  owned.sort(
    (a, b) => a[1].updatedAt - b[1].updatedAt || a[0].localeCompare(b[0]),
  )
  const next = { ...drafts }
  for (const [key] of owned.slice(0, owned.length - 50)) delete next[key]
  return next
}
